import { Link, Navigate } from "react-router-dom";
import {
  ArrowRight,
  BarChart3,
  Box,
  CheckCircle2,
  ClipboardList,
  Package,
  Shield,
  Truck,
  Users,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { useAuth } from "@/state/AuthContext";

const FEATURES = [
  {
    icon: Box,
    title: "Parts & systems",
    description: "Track parts, BOMs, and buildable quantities across programs in one dashboard.",
  },
  {
    icon: Truck,
    title: "Equipment workflows",
    description: "Receiving, shipping, checkouts, and returns — with full transaction history.",
  },
  {
    icon: ClipboardList,
    title: "Operational clarity",
    description: "Role-based access for teams. Everyone sees what they need, nothing they don't.",
  },
  {
    icon: BarChart3,
    title: "Reports built in",
    description: "Weekly summaries, low-stock alerts, and a flexible report builder.",
  },
];

const PLANS = [
  { name: "Starter", price: 49, seats: "5 users", highlight: false },
  { name: "Professional", price: 99, seats: "10 users", highlight: true },
  { name: "Business", price: 249, seats: "25 users", highlight: false },
  { name: "Enterprise", price: 499, seats: "Unlimited", highlight: false },
];

const STEPS = [
  "Create your organization",
  "Add parts and systems",
  "Run receiving, shipping, and checkouts",
];

export function LandingPage() {
  const { token, loading } = useAuth();

  if (!loading && token) {
    return <Navigate to="/dashboard" replace />;
  }

  return (
    <div className="min-h-svh bg-[#f4f7fa] text-[#1a2e3b]">
      {/* Nav */}
      <header className="sticky top-0 z-50 border-b border-[#1e3a4f]/10 bg-white/80 backdrop-blur-md">
        <div className="mx-auto flex max-w-6xl items-center justify-between px-4 py-4 sm:px-6">
          <Link to="/" className="text-xl font-bold tracking-tight text-[#1e3a4f]">
            InFloTrack
          </Link>
          <nav className="hidden items-center gap-8 text-sm font-medium text-[#1e3a4f]/80 md:flex">
            <a href="#features" className="hover:text-[#1e3a4f] transition-colors">
              Features
            </a>
            <a href="#pricing" className="hover:text-[#1e3a4f] transition-colors">
              Pricing
            </a>
          </nav>
          <div className="flex items-center gap-2 sm:gap-3">
            <Button variant="ghost" className="text-[#1e3a4f]" asChild>
              <Link to="/login">Sign in</Link>
            </Button>
            <Button className="bg-[#1e3a4f] hover:bg-[#163041] text-white" asChild>
              <Link to="/signup">
                Start free trial
                <ArrowRight className="size-4" />
              </Link>
            </Button>
          </div>
        </div>
      </header>

      {/* Hero */}
      <section className="relative overflow-hidden">
        <div
          className="absolute inset-0 -z-10 opacity-40"
          style={{
            background:
              "radial-gradient(ellipse 80% 60% at 50% -10%, #1e3a4f33 0%, transparent 70%)",
          }}
        />
        <div className="mx-auto max-w-6xl px-4 py-20 sm:px-6 sm:py-28 lg:py-32">
          <div className="mx-auto max-w-3xl text-center">
            <p className="mb-4 inline-flex items-center gap-2 rounded-full border border-[#1e3a4f]/15 bg-white px-4 py-1.5 text-sm font-medium text-[#1e3a4f] shadow-sm">
              <Shield className="size-4" />
              7-day Starter trial · No credit card required
            </p>
            <h1 className="text-4xl font-bold tracking-tight text-[#1e3a4f] sm:text-5xl lg:text-6xl">
              Inventory operations,{" "}
              <span className="text-[#2d6a8f]">without the spreadsheet chaos</span>
            </h1>
            <p className="mt-6 text-lg leading-relaxed text-[#1e3a4f]/70 sm:text-xl">
              InFloTrack helps teams manage parts, receiving, shipping, and checkouts —
              with real-time on-hand visibility and reports your whole org can trust.
            </p>
            <div className="mt-10 flex flex-col items-center justify-center gap-4 sm:flex-row">
              <Button size="lg" className="bg-[#1e3a4f] hover:bg-[#163041] text-white px-8 h-12 text-base" asChild>
                <Link to="/signup">
                  Start your 7-day trial
                  <ArrowRight className="size-5" />
                </Link>
              </Button>
              <Button size="lg" variant="outline" className="border-[#1e3a4f]/20 text-[#1e3a4f] h-12 px-8 text-base" asChild>
                <Link to="/login">Sign in to your account</Link>
              </Button>
            </div>
          </div>

          {/* Dashboard preview mock */}
          <div className="mx-auto mt-16 max-w-4xl">
            <div className="rounded-xl border border-[#1e3a4f]/10 bg-white p-1 shadow-2xl shadow-[#1e3a4f]/10">
              <div className="rounded-lg bg-[#1e3a4f] px-4 py-3 flex items-center gap-2">
                <div className="size-3 rounded-full bg-white/20" />
                <div className="size-3 rounded-full bg-white/20" />
                <div className="size-3 rounded-full bg-white/20" />
                <span className="ml-2 text-xs text-white/60 font-medium">InFloTrack Dashboard</span>
              </div>
              <div className="grid gap-3 p-4 sm:grid-cols-3 bg-[#e8eef3]">
                {[
                  { name: "Program Alpha", buildable: 6 },
                  { name: "Program Beta", buildable: 3 },
                  { name: "Program Gamma", buildable: 8 },
                ].map(({ name, buildable }) => (
                  <div key={name} className="rounded-lg bg-white p-4 shadow-sm">
                    <div className="flex items-center justify-between mb-3">
                      <span className="font-semibold text-sm text-[#1e3a4f]">{name}</span>
                      <span className="rounded bg-[#1e3a4f] px-2 py-0.5 text-xs font-bold text-white tabular-nums">
                        {buildable}
                      </span>
                    </div>
                    <div className="space-y-1.5">
                      {[1, 2, 3].map((i) => (
                        <div key={i} className="flex justify-between text-xs text-[#1e3a4f]/60">
                          <span>Part {String.fromCharCode(64 + i)}</span>
                          <span className="tabular-nums">{12 - i * 2} on-hand</span>
                        </div>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Features */}
      <section id="features" className="border-t border-[#1e3a4f]/10 bg-white py-20 sm:py-24">
        <div className="mx-auto max-w-6xl px-4 sm:px-6">
          <div className="mx-auto max-w-2xl text-center">
            <h2 className="text-3xl font-bold tracking-tight text-[#1e3a4f] sm:text-4xl">
              Everything your ops team needs
            </h2>
            <p className="mt-4 text-[#1e3a4f]/70">
              From receiving inventory to fulfilling shipping — one platform, your data, your organization.
            </p>
          </div>
          <div className="mt-14 grid gap-8 sm:grid-cols-2 lg:grid-cols-4">
            {FEATURES.map(({ icon: Icon, title, description }) => (
              <div
                key={title}
                className="group rounded-xl border border-[#1e3a4f]/8 bg-[#f4f7fa] p-6 transition-shadow hover:shadow-md"
              >
                <div className="mb-4 inline-flex rounded-lg bg-[#1e3a4f] p-3 text-white">
                  <Icon className="size-5" />
                </div>
                <h3 className="font-semibold text-[#1e3a4f]">{title}</h3>
                <p className="mt-2 text-sm leading-relaxed text-[#1e3a4f]/65">{description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* How it works */}
      <section className="py-20 sm:py-24">
        <div className="mx-auto max-w-6xl px-4 sm:px-6">
          <div className="grid items-center gap-12 lg:grid-cols-2">
            <div>
              <h2 className="text-3xl font-bold tracking-tight text-[#1e3a4f] sm:text-4xl">
                Up and running in minutes
              </h2>
              <p className="mt-4 text-[#1e3a4f]/70 leading-relaxed">
                Sign up with your organization name, invite your team, and start tracking inventory
                the same day. Multi-tenant by design — your data stays isolated and secure.
              </p>
              <ul className="mt-8 space-y-4">
                {STEPS.map((step, i) => (
                  <li key={step} className="flex items-start gap-3">
                    <span className="flex size-7 shrink-0 items-center justify-center rounded-full bg-[#1e3a4f] text-xs font-bold text-white">
                      {i + 1}
                    </span>
                    <span className="pt-0.5 font-medium text-[#1e3a4f]">{step}</span>
                  </li>
                ))}
              </ul>
            </div>
            <div className="grid grid-cols-2 gap-4">
              {[
                { icon: Package, label: "Receiving" },
                { icon: ClipboardList, label: "Shipping" },
                { icon: Truck, label: "Check-outs" },
                { icon: Users, label: "Team access" },
              ].map(({ icon: Icon, label }) => (
                <div
                  key={label}
                  className="flex flex-col items-center justify-center rounded-xl border border-[#1e3a4f]/10 bg-white p-8 shadow-sm"
                >
                  <Icon className="size-8 text-[#1e3a4f] mb-3" />
                  <span className="text-sm font-semibold text-[#1e3a4f]">{label}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Pricing */}
      <section id="pricing" className="border-t border-[#1e3a4f]/10 bg-white py-20 sm:py-24">
        <div className="mx-auto max-w-6xl px-4 sm:px-6">
          <div className="mx-auto max-w-2xl text-center">
            <h2 className="text-3xl font-bold tracking-tight text-[#1e3a4f] sm:text-4xl">
              Simple, predictable pricing
            </h2>
            <p className="mt-4 text-[#1e3a4f]/70">
              All plans include full features. Choose based on team size — upgrade anytime.
            </p>
          </div>
          <div className="mt-14 grid gap-6 sm:grid-cols-2 lg:grid-cols-4">
            {PLANS.map((plan) => (
              <div
                key={plan.name}
                className={`relative flex flex-col rounded-xl border p-6 ${
                  plan.highlight
                    ? "border-[#1e3a4f] bg-[#1e3a4f] text-white shadow-xl shadow-[#1e3a4f]/20"
                    : "border-[#1e3a4f]/10 bg-[#f4f7fa]"
                }`}
              >
                {plan.highlight && (
                  <span className="absolute -top-3 left-1/2 -translate-x-1/2 rounded-full bg-[#2d6a8f] px-3 py-0.5 text-xs font-semibold text-white">
                    Most popular
                  </span>
                )}
                <h3 className={`font-semibold ${plan.highlight ? "text-white" : "text-[#1e3a4f]"}`}>
                  {plan.name}
                </h3>
                <p className="mt-3">
                  <span className={`text-4xl font-bold ${plan.highlight ? "text-white" : "text-[#1e3a4f]"}`}>
                    ${plan.price}
                  </span>
                  <span className={plan.highlight ? "text-white/70" : "text-[#1e3a4f]/60"}>/mo</span>
                </p>
                <p className={`mt-1 text-sm ${plan.highlight ? "text-white/80" : "text-[#1e3a4f]/65"}`}>
                  {plan.seats}
                </p>
                <ul className="mt-6 flex-1 space-y-2 text-sm">
                  {["Full platform access", "All workflows", "Email support"].map((item) => (
                    <li key={item} className="flex items-center gap-2">
                      <CheckCircle2 className={`size-4 shrink-0 ${plan.highlight ? "text-white/90" : "text-[#1e3a4f]"}`} />
                      <span className={plan.highlight ? "text-white/90" : "text-[#1e3a4f]/75"}>{item}</span>
                    </li>
                  ))}
                </ul>
                <Button
                  className={`mt-6 w-full ${
                    plan.highlight
                      ? "bg-white text-[#1e3a4f] hover:bg-white/90"
                      : "bg-[#1e3a4f] hover:bg-[#163041] text-white"
                  }`}
                  asChild
                >
                  <Link to="/signup">Start trial</Link>
                </Button>
              </div>
            ))}
          </div>
          <p className="mt-8 text-center text-sm text-[#1e3a4f]/60">
            Every new organization starts with a 7-day Starter trial. No credit card required.
          </p>
        </div>
      </section>

      {/* CTA */}
      <section className="py-20 sm:py-24">
        <div className="mx-auto max-w-4xl px-4 sm:px-6">
          <div className="rounded-2xl bg-[#1e3a4f] px-8 py-14 text-center shadow-xl sm:px-16">
            <h2 className="text-3xl font-bold text-white sm:text-4xl">
              Ready to get control of your inventory?
            </h2>
            <p className="mx-auto mt-4 max-w-xl text-white/75">
              Join teams using InFloTrack to streamline equipment operations and keep parts visible across every program.
            </p>
            <Button size="lg" className="mt-8 bg-white text-[#1e3a4f] hover:bg-white/90 h-12 px-8 text-base" asChild>
              <Link to="/signup">
                Create your organization
                <ArrowRight className="size-5" />
              </Link>
            </Button>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="border-t border-[#1e3a4f]/10 bg-white py-10">
        <div className="mx-auto flex max-w-6xl flex-col items-center justify-between gap-4 px-4 sm:flex-row sm:px-6">
          <span className="font-bold text-[#1e3a4f]">InFloTrack</span>
          <p className="text-sm text-[#1e3a4f]/55">
            © {new Date().getFullYear()} InFloTrack. Inventory operations, simplified.
          </p>
          <div className="flex gap-6 text-sm text-[#1e3a4f]/70">
            <Link to="/login" className="hover:text-[#1e3a4f]">Sign in</Link>
            <Link to="/signup" className="hover:text-[#1e3a4f]">Sign up</Link>
          </div>
        </div>
      </footer>
    </div>
  );
}
